import React, { useState, useEffect, ChangeEvent } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'

import {
  FormControl,
  TextInput,
  InputGroup,
  Dropdown,
  ImageUpload,
  Loader,
  Switch,
} from 'components'
import {
  formatPhoneNumberOnBlur,
  getPhoneNumberMaxLength,
  validateKeyPress,
  trimFirstSpace,
  handleDisableOnPaste,
  thaiCountryCodeOption,
  statusFilter,
} from 'helpers/common'
import type { CompanyForForm, OptionProps } from 'types'

import { findSelected } from '../domain'

const CompanyInfo: React.FC = (): JSX.Element => {
  const { control, errors, setValue, watch, trigger, getValues } =
    useFormContext<CompanyForForm>()
  const [countryCodeOptions, setCountryCodeOptions] = useState<OptionProps[]>([
    thaiCountryCodeOption,
  ])
  const [nameChecking, setNameChecking] = useState<boolean>(false)
  const [taxIdChecking, setTaxIdChecking] = useState<boolean>(false)
  const [logo, setLogo] = useState('')

  const { code, countryCode } = watch(['code', 'countryCode'])
  const { t } = useTranslation(['company', 'common'])
  const base = 'f-company__form'

  const handleLogoChange = (blob: Blob): void => {
    setValue('logo', blob, { shouldDirty: true })
    setLogo(URL.createObjectURL(blob))
  }

  useEffect(() => {
    const currentCountryCodeOption: OptionProps<string> = {
      key: countryCode,
      label: countryCode,
    }

    const isCountryCodeOptionInclude = countryCodeOptions.find((option) =>
      isEqual(option, currentCountryCodeOption),
    )

    if (isCountryCodeOptionInclude) {
      return
    }

    setCountryCodeOptions([...countryCodeOptions, currentCountryCodeOption])
  }, [countryCode])

  const renderFieldCompanyName = (
    <FormControl
      htmlFor='company name'
      label={t('company:name.label')}
      className={`${base}__item`}
      required
    >
      <Controller
        type='text'
        name='name'
        control={control}
        render={(field) => (
          <TextInput
            value={field.value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              field.onChange(trimFirstSpace(e))
              trigger('name')
            }}
            onBlur={async () => {
              setNameChecking(true)
              setValue('name', getValues('name').trim())
              setValue('currentState', 'name')
              await trigger('name')
              setValue('currentState', '')
              setNameChecking(false)
            }}
            placeholder={t('company:name.placeholder')}
            isError={Boolean(errors.name?.message)}
            message={errors.name?.message}
            maxLength={100}
            withIcon={nameChecking && <Loader />}
            ref={field.ref}
          />
        )}
      />
    </FormControl>
  )

  const setOriginalValue = (
    <div style={{ display: 'none' }}>
      <Controller
        as={TextInput}
        type='hidden'
        name='currentState'
        control={control}
      />

      <Controller
        as={TextInput}
        type='hidden'
        name='originalName'
        control={control}
      />

      <Controller
        as={TextInput}
        type='hidden'
        name='originalTaxId'
        control={control}
      />
    </div>
  )

  return (
    <div className={`${base}__section`}>
      <div className={`${base}__logo-layout`}>
        <span className={`${base}__title`}>{t('company:info.label')}</span>

        {setOriginalValue}

        <FormControl htmlFor='company log' label={t('company:logo.label')}>
          <Controller
            control={control}
            name='logo'
            render={(fields) => (
              <ImageUpload
                className={`${base}__logo-size`}
                onChange={handleLogoChange}
                src={logo || fields.value}
                placeholder={t('company:logo.placeholder')}
              />
            )}
          />
        </FormControl>
      </div>

      <div className={`${base}__info`}>
        {code && (
          <div className={`${base}__code-layout ${base}__item`}>
            <span className={`${base}__code-title`}>
              {t('company:code.label')}
            </span>
            <span className={`${base}__code-field`}>{code}</span>
          </div>
        )}

        {!code && renderFieldCompanyName}

        <FormControl
          className={`${base}__item`}
          label={t('company:status.label')}
          required
        >
          <Controller
            name='isActive'
            control={control}
            render={(fields) => (
              <Switch
                className={`${base}__switch`}
                label={statusFilter(fields.value)}
                checked={fields.value}
                onChange={(e) => fields.onChange(e.target.checked)}
              />
            )}
          />
        </FormControl>

        {code && renderFieldCompanyName}

        <FormControl
          htmlFor='company id'
          label={t('common:taxId.label')}
          className={`${base}__item`}
          required
        >
          <Controller
            type='text'
            name='taxId'
            control={control}
            render={(field) => (
              <TextInput
                value={field.value}
                onChange={(e) => field.onChange(trimFirstSpace(e))}
                onBlur={async () => {
                  setTaxIdChecking(true)
                  setValue('currentState', 'taxId')
                  await trigger('taxId')
                  setValue('currentState', '')
                  setTaxIdChecking(false)
                }}
                placeholder={t('common:taxId.placeholder')}
                isError={Boolean(errors.taxId?.message)}
                message={errors.taxId?.message}
                maxLength={20}
                onKeyPress={validateKeyPress(new RegExp(/[\p{L}\p{N}]/u))}
                withIcon={taxIdChecking && <Loader />}
                ref={field.ref}
              />
            )}
          />
        </FormControl>

        <FormControl
          htmlFor='company email'
          label={t('common:email.label')}
          className={`${base}__item`}
          required
        >
          <Controller
            type='text'
            name='email'
            control={control}
            maxLength={50}
            render={(field) => (
              <TextInput
                value={field.value}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  field.onChange(trimFirstSpace(e))
                }
                onBlur={async () => {
                  setValue('email', getValues('email').trim())
                  await trigger('email')
                }}
                placeholder={t('common:email.placeholder')}
                isError={Boolean(errors.email?.message)}
                message={errors.email?.message}
                maxLength={50}
                ref={field.ref}
              />
            )}
          />
        </FormControl>

        <FormControl
          className={`${base}__item`}
          htmlFor='company telephone'
          label={t('common:phone.label')}
        >
          <InputGroup
            variants='ternary'
            className={`${base}__phone-group`}
            error={errors.countryCode?.message || errors.phoneNumber?.message}
          >
            <Controller
              type='text'
              name='countryCode'
              control={control}
              render={(field) => {
                return (
                  <Dropdown
                    className={'dropdown--telephone'}
                    group='right'
                    options={countryCodeOptions}
                    selected={findSelected(countryCodeOptions, field.value)}
                    onChange={(selected) => field.onChange(selected.key)}
                    isError={
                      Boolean(errors.countryCode?.message) ||
                      Boolean(errors.phoneNumber?.message)
                    }
                  />
                )
              }}
            />

            <Controller
              name='phoneNumber'
              control={control}
              render={(field) => {
                return (
                  <TextInput
                    className={`${base}__phone-field`}
                    placeholder={t('common:phone.placeholder')}
                    type='text'
                    group='left'
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      field.onChange(e.target.value)
                    }
                    value={field.value}
                    isError={
                      Boolean(errors.countryCode?.message) ||
                      Boolean(errors.phoneNumber?.message)
                    }
                    maxLength={getPhoneNumberMaxLength(field.value)}
                    onBlur={formatPhoneNumberOnBlur((value) => {
                      setValue('phoneNumber', value)
                      trigger('phoneNumber')
                    })}
                    onFocus={() =>
                      setValue('phoneNumber', field.value.replace('-', ''))
                    }
                    onKeyPress={validateKeyPress(new RegExp(/[\p{N}]/u))}
                    onPaste={(e) => {
                      handleDisableOnPaste(e)
                    }}
                  />
                )
              }}
            />
          </InputGroup>
        </FormControl>
      </div>
    </div>
  )
}

export default CompanyInfo
